import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Artifacts } from '@/api/EmissionReportModel'
import CombustionCalculatorPanel from '../calculators/CombustionCalculatorPanel'
import { Card, Table, TableColumnHeader, TableRow } from '@/common'
import { EmissionTypeToReport } from './ProcessEmissionsSection'
import { ReportSectionCardHeader } from '../SectionCardHeader'
import { EmissionsIcon } from '@/common/components/icons'
import CombustionProcessEmissionsTableRow from './CombustionProcessEmissionsTableRow'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'

type EmissionEditState = {
  productionRouteAssetId: number
  emissionRecordId?: string
}

export default function CombustionProcessEmissionReporting({
  isViewMode,
  emissionsToReport,
  onEmissionSubmit,
  onEmissionRemove
}: {
  emissionsToReport: EmissionTypeToReport[]
  isViewMode: boolean
  onEmissionSubmit: (
    payload: {
      emissionType: string
      method: string
      emission: number
      artifacts: Artifacts
      asset: ProductionRouteAsset
    },
    id?: string
  ) => void
  onEmissionRemove: (emissionRecordId: string) => void
  isComplete: boolean
}) {
  const { t } = useTranslation()
  const totalReportedEmissions = useMemo(() => {
    return emissionsToReport.reduce(
      (acc, emission) => acc + (emission.emissionRecord?.emission || 0),
      0
    )
  }, [emissionsToReport])

  const [editState, setEditState] = useState<EmissionEditState | null>(null)

  const editedProductionRouteAsset = useMemo(() => {
    if (editState === null) {
      return null
    }

    return (
      emissionsToReport.find(
        emission => emission.productionRouteAsset.id === editState.productionRouteAssetId
      ) || null
    )
  }, [editState, emissionsToReport])

  const onSubmit = useCallback(
    (emission: number, artifacts: Artifacts) => {
      if (editedProductionRouteAsset === null) {
        return
      }

      const existingRecord = editedProductionRouteAsset.emissionRecord

      onEmissionSubmit(
        {
          asset: editedProductionRouteAsset.productionRouteAsset,
          emissionType: 'COMBUSTION',
          method: 'COMBUSTION',
          emission,
          artifacts
        },
        existingRecord?.id
      )
    },
    [editedProductionRouteAsset, onEmissionSubmit]
  )

  const percentageOfAssetsWithEmissions = useMemo(() => {
    const reportedCount = emissionsToReport.filter(
      emission => typeof emission.emissionRecord?.emission === 'number'
    ).length
    return (reportedCount / emissionsToReport.length) * 100
  }, [emissionsToReport])

  return (
    <>
      <Card>
        <ReportSectionCardHeader
          icon={<EmissionsIcon className="size-4" />}
          title={t('emissionTypes.COMBUSTION')}
          emissions={totalReportedEmissions}
          completionPercentage={percentageOfAssetsWithEmissions}
        />
        <Table dense borderless>
          <thead>
            <TableRow>
              <TableColumnHeader className="w-64" />
              <TableColumnHeader className="w-36">
                {t('report:processEmissions.table.headers.customId')}
              </TableColumnHeader>
              <TableColumnHeader className="w-24">
                {t('report:processEmissions.table.headers.fuelType')}
              </TableColumnHeader>
              <TableColumnHeader className="w-24 text-right">
                {t('report:processEmissions.table.headers.emissionFactor')}
              </TableColumnHeader>
              <TableColumnHeader className="w-24 text-right">
                {t('report:processEmissions.table.headers.fuelConsumed')}
              </TableColumnHeader>
              <TableColumnHeader className="w-24 text-right">
                {t('report:processEmissions.table.headers.emission')}
              </TableColumnHeader>
              <TableColumnHeader className="w-44" />
            </TableRow>
          </thead>
          {emissionsToReport.map(emission => (
            <CombustionProcessEmissionsTableRow
              key={emission.productionRouteAsset.id}
              assetWithMetadata={emission}
              setIsAddingEmission={setEditState}
              isViewMode={isViewMode}
              onEmissionRemove={onEmissionRemove}
            />
          ))}
        </Table>
      </Card>
      <CombustionCalculatorPanel
        submit={onSubmit}
        isOpen={editState !== null}
        close={() => setEditState(null)}
        initialValues={editedProductionRouteAsset?.emissionRecord?.artifacts}
      />
    </>
  )
}
